/**
 * «الان داری کدوم شهر رو می‌بینی؟»
 *
 * تا مایگریشن 0063، جوابِ این سوال یه رشته‌ی ثابت توی کد بود: `'ottawa'`.
 * حالا دو تا مقدار داریم و تفاوتشون مهمه:
 *
 *   • **شهرِ دیفالت** (`home`) — شهری که کاربر برای خودش انتخاب کرده
 *     (تنظیمات ← شهر). روی حسابش ذخیره می‌شه و بینِ دستگاه‌ها یکیه.
 *     کاربرِ بدونِ حساب روی وب اینو از IPش می‌گیره — نگاه کن به ipCity.ts.
 *
 *   • **شهرِ درحالِ‌تماشا** (`temp`) — چیزی که از دکمه‌ی کنارِ جستجو انتخاب
 *     شده. روی همین دستگاه می‌مونه (نه روی حساب) و **تا خودِ کاربر برش
 *     نگردونه، برنمی‌گرده**: با ردیفِ «برگرد به شهرِ خودم» توی همون منو، با
 *     انتخابِ دوباره‌ی شهرِ دیفالت، یا با عوض کردنِ دیفالت توی تنظیمات.
 *
 *     قبلاً با پاک شدنِ عبارتِ جستجو خودبه‌خود می‌پرید («کنجکاویه، نه
 *     تصمیم») — و در عمل یعنی تقریباً همیشه، چون کادرِ جستجو تقریباً همیشه
 *     خالیه و روی وب هر برگشتی به صفحه‌ی کشف از نو می‌سازدش. جوابِ ترسِ
 *     «سه روز بعد چرا دارم تورنتو می‌بینم؟» دیده‌شدنه، نه فراموشیِ خودکار:
 *     دکمه همیشه اسمِ شهر و نشانِ «غیرِ دیفالت» رو نشون می‌ده.
 *
 * الگو عمداً همون الگوی theme.ts و prefs.ts است: یه آبجکتِ **زنده** که سرِ
 * جاش پر می‌شه، به‌علاوه‌ی شنونده‌ها و یه شماره‌ی نسخه. چون فایل‌ها ممکنه
 * مقدار رو موقعِ بارگذاری بگیرن، جایگزین کردنِ خودِ آبجکت مرجع‌های قدیمی رو
 * کهنه می‌کنه.
 */
import { useEffect, useReducer } from 'react';
import { storage } from '../platform.ts';
import { recordPref } from './prefSink.ts';
import { distanceKm } from './discoverSort.ts';
import type { AppCity } from '../domain/types.ts';

/**
 * آخرین شهرِ دیفالت، روی همین دستگاه.
 *
 * چرا با اینکه روی حساب هم هست: اپ باید همون فریمِ اول بدونه کدوم شهر رو
 * بخواد. پروفایل از شبکه می‌آد و تا رسیدنش، بدونِ این، فهرست یا خالیه یا
 * شهرِ اشتباهیه که بعد می‌پره. برای کاربرِ بدونِ حساب هم یعنی جست‌وجوی IP
 * فقط یه‌بار انجام می‌شه، نه هر بار باز شدنِ صفحه.
 */
const KEY = 'whosup.city.v1';

/**
 * شهرِ درحالِ‌تماشا، جدا از دیفالت. کلیدِ جدا چون عمرشون فرق داره:
 * دیفالت رو پروفایل/IP هم می‌تونن بازنویسی کنن، این یکی فقط دستِ خودِ
 * کاربره.
 */
const TEMP_KEY = 'whosup.city.temp.v1';

interface Scope {
  home: AppCity | null;
  temp: AppCity | null;
}

const scope: Scope = { home: null, temp: null };

/**
 * «این دستگاه عمداً شهری یادش نیست» — از `forgetCity` (حذفِ حساب، WHOSUP-352)
 * تا اولین شهری که واقعاً معلوم شود: انتخابِ خودِ کاربر، یا شهرِ پروفایلِ
 * حسابی که واردش می‌شوند.
 *
 * چرا یک پرچم و نه فقط پاک کردن: بلافاصله بعد از خروج، cityBoot برای مهمان
 * دوباره یک شهر می‌نشاند (حدسِ IP یا اولین شهرِ فهرست) تا کشف خالی نماند. بی
 * این پرچم همان حدس روی دستگاه ذخیره و پیش‌پرِ فرمِ نمایه‌ی حسابِ بعدی می‌شد —
 * یعنی همان باگ، فقط با یک شهرِ دلبخواهِ دیگر. در این حالت حدس **نشان داده**
 * می‌شود ولی نه ذخیره، نه پیش‌پر (نگاه کن به `cityForgotten`).
 */
let forgotten = false;

let version = 0;
const listeners = new Set<() => void>();
const bump = () => { version += 1; listeners.forEach((fn) => fn()); };

export const cityVersion = (): number => version;

export function onCityChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** شهری که همین الان فهرست‌ها باید ازش پر شن. */
export const activeCity = (): AppCity | null => scope.temp ?? scope.home;

/** شهرِ دیفالت، جدا از موقت — تنظیمات همین رو نشون می‌ده. */
export const homeCity = (): AppCity | null => scope.home;

/** الان یه شهرِ موقت روی فهرست نشسته؟ (دکمه‌ی شهر با این نشان می‌گیره.) */
export const hasTempCity = (): boolean => scope.temp !== null;

/**
 * شهرِ فعلی فقط حدسِ بعد از `forgetCity` است؟ فرمِ ساختِ نمایه با این از
 * پیش‌پر کردن صرف‌نظر می‌کند: شهری که هیچ‌کس روی این دستگاه انتخاب نکرده،
 * جوابِ «کجا زندگی می‌کنی» نیست.
 */
export const cityForgotten = (): boolean => forgotten;

/**
 * هر دو شهر را از حافظه **و** از دستگاه برمی‌دارد — برای حسابی که حذف شد
 * (WHOSUP-352). بدونِ این، حسابِ بعدی روی همان گوشی شهرِ حسابِ رفته را در فرمِ
 * نمایه‌اش پیش‌پر می‌دید.
 *
 * عمداً `setHomeCity(null)` نیست: آن یکی `recordPref` می‌نویسد، و این‌جا
 * کسی شهری انتخاب نکرده — حساب دیگر وجود ندارد که تاریخچه‌ای داشته باشد.
 */
export function forgetCity(): void {
  scope.home = null;
  scope.temp = null;
  forgotten = true;
  bump();
  void storage().removeItem(KEY).catch(() => undefined);
  void storage().removeItem(TEMP_KEY).catch(() => undefined);
}

/** پایانِ حالتِ «فراموش‌شده»: شهرِ دیفالتی که تا حالا فقط نشان داده می‌شد، از این به بعد ذخیره هم می‌شود. */
function remember(): void {
  if (!forgotten) return;
  forgotten = false;
  if (scope.home) void storage().setItem(KEY, JSON.stringify(scope.home)).catch(() => undefined);
}

/**
 * شهرِ دیفالت رو می‌ذاره و روی دستگاه هم ذخیره‌ش می‌کنه.
 *
 * شهرِ درحالِ‌تماشا رو هم برمی‌داره (از حافظه **و** از دستگاه): کسی که
 * دیفالتش رو عوض کرده، همون رو می‌خواد ببینه — نه شهری که قبلاً داشت
 * تماشاش می‌کرد.
 */
export function setHomeCity(city: AppCity | null): void {
  if (scope.home?.id === city?.id && scope.temp === null && !forgotten) return;
  scope.home = city;
  scope.temp = null;
  forgotten = false;
  bump();
  void storage().setItem(KEY, JSON.stringify(city)).catch(() => undefined);
  void storage().removeItem(TEMP_KEY).catch(() => undefined);
  // فقط این‌جا و نه در `adoptHomeCity`: آن یکی آشتیِ خودکارِ بوت است و
  // انتخابِ کسی نیست، پس در تاریخچه‌ی انتخاب‌ها جایی ندارد.
  recordPref('cityHome', city ? { id: city.id, name: city.name } : null);
}

/**
 * نسخه‌ی **خودکارِ** setHomeCity — فقط برای آشتیِ بوت (cityBoot)، نه برای
 * کارِ کاربر.
 *
 * فرقش یک چیزه: شهرِ درحالِ‌تماشا رو نگه می‌داره. دیفالتی که از پروفایل یا
 * حدسِ IP می‌آد کارِ کاربر نیست و نباید انتخابِ صریحِ «دارم این شهر رو
 * می‌بینم» رو دور بریزه — این دقیقاً روی استیجینگ دیده شد: مهمان شهر عوض
 * می‌کرد، رفرش می‌کرد، و IP همون دیفالتِ قبلی رو دوباره می‌نشوند و از راهِ
 * setHomeCity انتخابش رو می‌پروند.
 *
 * تنها استثنا: دیفالتِ تازه خودش همون شهرِ درحالِ‌تماشا باشه — اون‌وقت
 * دیگه «درحالِ‌تماشا» معنی نداره و برداشته می‌شه، همون نرمال‌سازیِ همیشگی.
 *
 * `guess`: این شهر حدس است (IP یا اولین شهرِ فهرست)، نه شهرِ پروفایل. فرقش
 * فقط بعد از `forgetCity` پیدا می‌شه: حدس نشون داده می‌شه ولی روی دستگاه
 * نمی‌شینه، و شهرِ پروفایل حالتِ «فراموش‌شده» رو تموم می‌کنه.
 */
export function adoptHomeCity(city: AppCity | null, opts: { guess?: boolean } = {}): void {
  const homeChanged = scope.home?.id !== city?.id;
  const tempRedundant = !!city && scope.temp?.id === city.id;
  if (homeChanged || tempRedundant) {
    scope.home = city;
    if (tempRedundant) {
      scope.temp = null;
      void storage().removeItem(TEMP_KEY).catch(() => undefined);
    }
    bump();
    if (homeChanged && !forgotten) {
      void storage().setItem(KEY, JSON.stringify(city)).catch(() => undefined);
    }
  }
  if (!opts.guess && city) remember();
}

export function setTempCity(city: AppCity | null): void {
  if (scope.temp?.id === city?.id) return;
  // انتخابِ دوباره‌ی خودِ شهرِ دیفالت یعنی «برگرد سرِ جای اول»، نه یه لایه‌ی
  // موقتِ اضافه که ظاهرش با دیفالت فرقی نداره ولی نشانِ «موقت» می‌گیره.
  scope.temp = city && city.id === scope.home?.id ? null : city;
  // شهری که خودِ کاربر از منو برداشته، انتخاب است نه حدس — حتی اگر همان
  // دیفالتِ حدس‌زده باشد.
  if (city) remember();
  bump();
  // ⚠ فقط **تاریخچه** — شهرِ درحالِ‌تماشا مالِ همین دستگاه می‌ماند و روی
  // `profiles.city_id` نمی‌نشیند. سرور فقط می‌داند که این آدم کِی رفت
  // کدام شهر را تماشا کند، نه اینکه شهرش عوض شده. نگاه کن به lib/prefSink.
  recordPref('cityTemp', scope.temp
    ? { id: scope.temp.id, name: scope.temp.name }
    : null);
  // ذخیره‌ی نسخه‌ی نرمال‌شده، نه ورودی: چیزی که «برگرد سرِ جای اول» بود
  // نباید به اسمِ شهرِ درحالِ‌تماشا روی دستگاه بمونه.
  void (scope.temp
    ? storage().setItem(TEMP_KEY, JSON.stringify(scope.temp))
    : storage().removeItem(TEMP_KEY)
  ).catch(() => undefined);
}

export const clearTempCity = (): void => setTempCity(null);

/** شکلِ ذخیره‌شده سالمه؟ مقدارِ خراب یعنی هیچ شهری — همون مسیرِ کاربرِ تازه. */
const validCity = (v: AppCity | null): v is AppCity =>
  !!v && typeof v.id === 'number' && typeof v.name === 'string';

/**
 * مقدارهای ذخیره‌شده‌ی روی دستگاه رو برمی‌گردونه — دیفالت و درحالِ‌تماشا،
 * به همین ترتیب. قبل از اولین رندر صدا زده می‌شه (کنارِ initPrefs و
 * initLocale)، پس بی‌صدا شکست می‌خوره: شهرِ خونده‌نشده یعنی «هنوز معلوم
 * نیست»، نه اپی که بالا نمی‌آد.
 */
export async function initCityScope(): Promise<void> {
  try {
    const raw = await storage().getItem(KEY);
    // اگه تا رسیدنِ این جواب، شهرِ واقعی از پروفایل/IP معلوم شده باشه، دست
    // بهش نمی‌زنیم: خواندنِ حافظه‌ی دستگاه و پاسخِ شبکه با هم مسابقه می‌دن و
    // برنده باید تازه‌ترین باشه، نه دیرترین.
    if (raw && !scope.home) {
      const saved = JSON.parse(raw) as AppCity | null;
      if (validCity(saved)) {
        scope.home = saved;
        bump();
      }
    }
  } catch { /* مقدارِ خراب یعنی هیچ شهری. */ }
  try {
    const raw = await storage().getItem(TEMP_KEY);
    // `scope.temp` از قبل پر؟ یعنی کاربر زودتر از رسیدنِ این خواندن یکی
    // انتخاب کرده — تازه‌تر می‌بره، مثلِ همون قاعده‌ی بالا.
    if (!raw || scope.temp) return;
    const saved = JSON.parse(raw) as AppCity | null;
    if (!validCity(saved)) return;
    // ذخیره‌ای که با دیفالتِ فعلی یکیه، شهرِ درحالِ‌تماشا نیست — جامونده‌ست
    // (مثلاً دیفالت بعداً همون شهر شده). پاکش می‌کنیم که هر بوت برنگرده.
    if (saved.id === scope.home?.id) {
      void storage().removeItem(TEMP_KEY).catch(() => undefined);
      return;
    }
    scope.temp = saved;
    bump();
  } catch { /* همون قاعده. */ }
}

/**
 * نزدیک‌ترین شهرِ **دارای محتوا** به یه نقطه.
 *
 * تابعِ خالص و بی‌شبکه تا تست‌پذیر بمونه. شهری که مختصات نداره کنار گذاشته
 * می‌شه نه اینکه فاصله‌ی صفر بگیره — «نمی‌دونم کجاست» با «همین‌جاست» یکی
 * نیست، و ردیفِ بی‌مختصات وگرنه همیشه برنده می‌شد.
 */
export function nearestCity(
  origin: { lat: number; lng: number }, cities: AppCity[],
): AppCity | null {
  let best: AppCity | null = null;
  let bestKm = Infinity;
  for (const c of cities) {
    if (typeof c.lat !== 'number' || typeof c.lng !== 'number') continue;
    const km = distanceKm(origin, { lat: c.lat, lng: c.lng });
    if (km < bestKm) { bestKm = km; best = c; }
  }
  return best;
}

/**
 * از بینِ شهرهای اپ، اونی که با مختصاتِ داده‌شده جور در می‌آد.
 *
 * اگه خودِ اون شهر توی فهرست باشه همون؛ وگرنه نزدیک‌ترین. اولی به اسم چک
 * می‌شه نه به فاصله: سرویسِ IP معمولاً اسمِ شهر رو درست می‌ده ولی مختصاتش
 * وسطِ شهره، و «مرکزِ شهر» می‌تونه به شهرِ بغلی نزدیک‌تر بیفته.
 */
export function matchCity(
  found: { city?: string | null; lat?: number | null; lng?: number | null },
  cities: AppCity[],
): AppCity | null {
  const name = found.city?.trim().toLowerCase();
  if (name) {
    const exact = cities.find((c) => c.name.trim().toLowerCase() === name);
    if (exact) return exact;
  }
  if (typeof found.lat === 'number' && typeof found.lng === 'number') {
    return nearestCity({ lat: found.lat, lng: found.lng }, cities);
  }
  return null;
}

/** رندرِ دوباره با هر تغییرِ شهر — قرینه‌ی useLocale توی i18n.ts. */
export function useCityScope(): AppCity | null {
  const [, force] = useReducer((n: number) => n + 1, 0);
  useEffect(() => onCityChange(force), []);
  return activeCity();
}
